/**
 * Le pointage de la garderie sur le téléphone de l'animateur : `/garderie/:jeton`.
 *
 * Sans compte. Le jeton de l'adresse vaut pour une école et une année scolaire
 * (migration `20261031200000_le_pointage_de_la_garderie`) ; la base vérifie le
 * lien et le code à chaque geste. Cette page tient le code à quatre chiffres
 * (gardé le temps de la session du navigateur) et le prénom de l'animateur
 * (gardé sur le téléphone) ; l'écran lui-même est
 * `components/garderie/EcranDePointage.tsx`.
 *
 * Hors de toute mise en page : ni menu ni barre, l'écran d'un téléphone est
 * petit et l'animateur a des enfants devant lui.
 */
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LockKeyhole, Pencil, UserRound } from 'lucide-react';
import { EcranDePointage } from '../../components/garderie/EcranDePointage';
import { sourceDuLien } from '../../lib/garderie/pointage.supabase';

const cleDuCode = (jeton: string) => `garderie-code-${jeton}`;
const CLE_PRENOM = 'garderie-prenom';

const lire = (stockage: Storage, cle: string): string | null => {
  try {
    return stockage.getItem(cle);
  } catch {
    return null;
  }
};
const garder = (stockage: Storage, cle: string, valeur: string | null) => {
  try {
    if (valeur) stockage.setItem(cle, valeur);
    else stockage.removeItem(cle);
  } catch {
    /* navigation privée : on redemandera */
  }
};

export default function PointageLienPage() {
  const { jeton = '' } = useParams<{ jeton: string }>();
  const [code, setCode] = useState<string | null>(() => lire(sessionStorage, cleDuCode(jeton)));
  const [codeRequis, setCodeRequis] = useState(false);
  const [codeFaux, setCodeFaux] = useState(false);
  const [prenom, setPrenom] = useState<string | null>(() => lire(localStorage, CLE_PRENOM));
  const [changerPrenom, setChangerPrenom] = useState(false);

  const source = useMemo(() => sourceDuLien(jeton, code, prenom ?? ''), [jeton, code, prenom]);

  const onCodeRequis = useCallback(() => {
    setCodeFaux(code !== null);
    garder(sessionStorage, cleDuCode(jeton), null);
    setCodeRequis(true);
  }, [code, jeton]);

  if (codeRequis) {
    return (
      <div className="min-h-screen bg-gray-50 text-gray-900">
        <EcranDuCode
          codeFaux={codeFaux}
          onCode={(c) => {
            garder(sessionStorage, cleDuCode(jeton), c);
            setCode(c);
            setCodeRequis(false);
          }}
        />
      </div>
    );
  }

  if (!prenom || changerPrenom) {
    return (
      <div className="min-h-screen bg-gray-50 text-gray-900">
        <EcranDuPrenom
          actuel={prenom ?? ''}
          onPrenom={(p) => {
            garder(localStorage, CLE_PRENOM, p);
            setPrenom(p);
            setChangerPrenom(false);
          }}
        />
      </div>
    );
  }

  return (
    <EcranDePointage
      key={code ?? ''}
      source={source}
      sousTitre={
        <button type="button" onClick={() => setChangerPrenom(true)} className="flex items-center gap-1">
          {prenom} <Pencil className="h-3 w-3" />
        </button>
      }
      onCodeRequis={onCodeRequis}
    />
  );
}

function EcranDuCode({ codeFaux, onCode }: { codeFaux: boolean; onCode: (code: string) => void }) {
  const [codeTape, setCodeTape] = useState('');
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (codeTape.length === 4) {
          onCode(codeTape);
          setCodeTape('');
        }
      }}
      className="mx-4 mt-16 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200"
    >
      <LockKeyhole className="mx-auto h-10 w-10 text-primary-500" />
      <h1 className="mt-3 text-center text-lg font-bold text-gray-900">Code de l’école</h1>
      <p className="mt-1 text-center text-sm text-gray-600">Les quatre chiffres donnés par la coordination.</p>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]*"
        maxLength={4}
        value={codeTape}
        onChange={(e) => setCodeTape(e.target.value.replace(/\D/g, '').slice(0, 4))}
        autoFocus
        className="mt-5 w-full rounded-xl border border-gray-300 px-4 py-4 text-center text-3xl tracking-[0.5em] focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
        aria-label="Code à quatre chiffres"
      />
      {codeFaux && <p className="mt-2 text-center text-sm text-red-600">Ce code n’est pas le bon.</p>}
      <button type="submit" disabled={codeTape.length !== 4} className="mt-4 w-full rounded-xl bg-primary-600 px-4 py-4 text-lg font-semibold text-white disabled:opacity-40">
        Ouvrir la liste
      </button>
    </form>
  );
}

function EcranDuPrenom({ actuel, onPrenom }: { actuel: string; onPrenom: (prenom: string) => void }) {
  const [prenom, setPrenom] = useState(actuel);
  const propre = prenom.trim();
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (propre) onPrenom(propre.slice(0, 60));
      }}
      className="mx-4 mt-16 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200"
    >
      <UserRound className="mx-auto h-10 w-10 text-primary-500" />
      <h1 className="mt-3 text-center text-lg font-bold text-gray-900">Ton prénom</h1>
      <p className="mt-1 text-center text-sm text-gray-600">Il est noté avec chaque présence que tu pointes.</p>
      <input
        type="text"
        autoComplete="given-name"
        value={prenom}
        onChange={(e) => setPrenom(e.target.value)}
        autoFocus
        className="mt-5 w-full rounded-xl border border-gray-300 px-4 py-3 text-center text-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
        aria-label="Prénom"
      />
      <button type="submit" disabled={!propre} className="mt-4 w-full rounded-xl bg-primary-600 px-4 py-4 text-lg font-semibold text-white disabled:opacity-40">
        C’est parti
      </button>
    </form>
  );
}
